import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Archive, Download, MagnifyingGlass, Scales, ClockCounterClockwise, Trash } from "@phosphor-icons/react";

const COLL_LABELS = {
  evidence: "Evidence", form_submissions: "Form submissions", narratives: "RSAW narratives",
  tasks: "Tasks", notifications: "Notifications", audit_logs: "Audit logs",
};

export default function RetentionManager() {
  const [years, setYears] = useState(7);
  const [savedYears, setSavedYears] = useState(7);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [backedUp, setBackedUp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);

  // Activity / audit-log retention (day-based)
  const [logLogin, setLogLogin] = useState(180);
  const [logOther, setLogOther] = useState(365);
  const [savedLog, setSavedLog] = useState({ login_days: 180, other_days: 365 });
  const [logPreview, setLogPreview] = useState(null);
  const [logConfirm, setLogConfirm] = useState("");
  const [logResult, setLogResult] = useState(null);
  const [busyLog, setBusyLog] = useState(false);

  const loadPolicy = () => api.get("/admin/retention-policy").then((r) => { setYears(r.data.years); setSavedYears(r.data.years); }).catch(() => {});
  const loadLogPolicy = () => api.get("/admin/log-retention-policy").then((r) => {
    setLogLogin(r.data.login_days); setLogOther(r.data.other_days);
    setSavedLog({ login_days: r.data.login_days, other_days: r.data.other_days });
  }).catch(() => {});
  const loadArchive = () => api.get("/admin/retention-archive").then((r) => setBatches(r.data)).catch(() => {});
  useEffect(() => { loadPolicy(); loadLogPolicy(); loadArchive(); }, []);

  const saveLogPolicy = async () => {
    try {
      const { data } = await api.put("/admin/log-retention-policy", { login_days: Number(logLogin), other_days: Number(logOther) });
      setSavedLog({ login_days: data.login_days, other_days: data.other_days });
      setLogLogin(data.login_days); setLogOther(data.other_days); setLogPreview(null);
      toast.success(`Log retention saved — sessions ${data.login_days}d, activity ${data.other_days}d`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runLogPreview = async () => {
    setBusyLog(true);
    try { const { data } = await api.post("/admin/log-retention-preview"); setLogPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyLog(false); }
  };
  const runLogPurge = async () => {
    setBusyLog(true);
    try {
      const { data } = await api.post("/admin/log-retention-purge", { confirm: logConfirm });
      setLogResult(data); setLogConfirm(""); setLogPreview(null);
      toast.success(`Purged ${data.archived} log(s) — recoverable from the archive below`);
      loadArchive();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyLog(false); }
  };

  const savePolicy = async () => {
    try {
      const { data } = await api.put("/admin/retention-policy", { years: Number(years) });
      setSavedYears(data.years); setYears(data.years); setPreview(null);
      toast.success(`Retention set to ${data.years} calendar years`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/retention-preview"); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadBackup = async () => {
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `claro_backup_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      setBackedUp(true);
      toast.success("Backup downloaded");
    } catch (e) { toast.error("Backup failed"); }
  };

  const runPurge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/retention-purge", { confirm });
      setResult(data); setConfirm(""); setBackedUp(false); setPreview(null);
      toast.success(`Purged ${data.archived} record(s) — recoverable from the archive below`);
      loadArchive();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const restoreBatch = async (bid) => {
    try {
      const { data } = await api.post(`/admin/retention-archive/${bid}/restore`);
      toast.success(`Restored ${data.restored} record(s)`);
      loadArchive();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cutoff = new Date().getFullYear() - savedYears;

  return (
    <div className="max-w-3xl space-y-6">
      {/* Policy */}
      <div className="border border-border bg-card p-5" data-testid="retention-policy">
        <div className="font-display font-bold text-sm mb-1">Retention Policy</div>
        <p className="text-sm text-muted-foreground mb-3">
          Records are kept through <span className="font-semibold">December 31</span> of the year they occurred plus the retention period, then become eligible for purge.
          Default is <span className="font-semibold">7 calendar years</span>; override as your obligations require. <span className="text-amber-700 font-medium">Legal Hold material is never purged.</span>
        </p>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">Retention period (calendar years)</Label>
            <Input data-testid="retention-years-input" type="number" min="1" max="99" value={years}
              onChange={(e) => setYears(e.target.value)} className="rounded-none mt-1 w-28" />
          </div>
          <Button data-testid="save-retention-btn" onClick={savePolicy} className="rounded-none bg-primary hover:bg-primary/90">Save Policy</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 font-mono">At {savedYears} yrs (today {new Date().getFullYear()}): records dated <b>{cutoff}</b> or earlier are eligible.</p>
      </div>

      {/* Activity / audit-log retention (day-based) */}
      <div className="border border-border bg-card p-5" data-testid="log-retention-policy">
        <div className="font-display font-bold text-sm mb-1">Activity Log Retention</div>
        <p className="text-sm text-muted-foreground mb-3">
          Applies only to the <span className="font-semibold">system activity / audit log</span> (Admin → Audit Trail) — not your evidence, documents, RSAWs or signatures.
          Set how long each category of log is kept; older entries become eligible for a manual, recoverable purge.
          <span className="block mt-1"><span className="font-semibold">Sessions</span> default 180 days; <span className="font-semibold">other activity</span> defaults to 365 days (1-year baseline) — raise either as your obligations require.</span>
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs">Login / session events (days)</Label>
            <Input data-testid="log-login-days-input" type="number" min="1" max="3650" value={logLogin}
              onChange={(e) => setLogLogin(e.target.value)} className="rounded-none mt-1 w-32" />
          </div>
          <div>
            <Label className="text-xs">Other activity logs (days)</Label>
            <Input data-testid="log-other-days-input" type="number" min="1" max="3650" value={logOther}
              onChange={(e) => setLogOther(e.target.value)} className="rounded-none mt-1 w-32" />
          </div>
          <Button data-testid="save-log-retention-btn" onClick={saveLogPolicy} className="rounded-none bg-primary hover:bg-primary/90">Save Log Policy</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 font-mono">
          Saved: sessions <b>{savedLog.login_days}d</b> · other activity <b>{savedLog.other_days}d</b>.
        </p>

        <div className="mt-4 border-t border-border pt-4">
          <Button data-testid="log-retention-preview-btn" onClick={runLogPreview} disabled={busyLog} variant="outline" className="rounded-none"><MagnifyingGlass size={16} className="mr-1" />Preview Eligible Logs</Button>
          {logPreview && (
            <div className="mt-3 border border-border" data-testid="log-retention-preview-result">
              <div className="px-3 py-2 bg-secondary/40 text-sm font-semibold">{logPreview.total_eligible} log(s) eligible for purge</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-3 text-sm">
                <div className={logPreview.eligible.login_sessions > 0 ? "" : "text-muted-foreground"}><span className="text-muted-foreground">Login / sessions (&gt;{logPreview.login_days}d):</span> <span className="font-bold">{logPreview.eligible.login_sessions}</span></div>
                <div className={logPreview.eligible.other_activity > 0 ? "" : "text-muted-foreground"}><span className="text-muted-foreground">Other activity (&gt;{logPreview.other_days}d):</span> <span className="font-bold">{logPreview.eligible.other_activity}</span></div>
              </div>
              {logPreview.total_eligible > 0 && (
                <div className="border-t-2 border-amber-500 bg-amber-50 p-4 space-y-3">
                  <p className="text-sm text-amber-800">Purged logs are moved to the recoverable archive (below) — not permanently deleted.</p>
                  <div className="flex gap-2 max-w-md">
                    <Input data-testid="log-retention-confirm-input" value={logConfirm} onChange={(e) => setLogConfirm(e.target.value)} placeholder="Type PURGE" className="rounded-none border-amber-400 bg-card" />
                    <Button data-testid="run-log-purge-btn" onClick={runLogPurge} disabled={busyLog || logConfirm.trim() !== "PURGE"}
                      className="rounded-none bg-amber-600 hover:bg-amber-700 whitespace-nowrap"><Trash size={16} className="mr-1" />{busyLog ? "Purging…" : "Run Log Purge"}</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {logResult && (
            <div className="mt-3 border border-border p-3 text-sm" data-testid="log-retention-purge-result">
              <div className="font-semibold mb-1">Log purge complete — {logResult.archived} archived</div>
              <div className="font-mono text-xs text-muted-foreground">batch {logResult.batch_id}</div>
            </div>
          )}
        </div>
      </div>

      {/* Preview & Purge */}
      <div className="border border-border bg-card p-5">
        <div className="font-display font-bold text-sm mb-3 flex items-center gap-2"><MagnifyingGlass size={16} />Preview &amp; Purge</div>
        <Button data-testid="retention-preview-btn" onClick={runPreview} disabled={busy} variant="outline" className="rounded-none"><MagnifyingGlass size={16} className="mr-1" />Preview Eligible Records</Button>

        {preview && (
          <div className="mt-4 border border-border" data-testid="retention-preview-result">
            <div className="px-3 py-2 bg-secondary/40 text-sm font-semibold flex items-center justify-between">
              <span>{preview.total_eligible} record(s) eligible for purge</span>
              {preview.skipped_due_to_legal_hold > 0 && <span className="flex items-center gap-1 text-amber-700 text-xs"><Scales size={13} weight="fill" />{preview.skipped_due_to_legal_hold} skipped (Legal Hold)</span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 p-3 text-sm">
              {Object.entries(preview.eligible).map(([k, v]) => (
                <div key={k} className={v > 0 ? "" : "text-muted-foreground"}><span className="text-muted-foreground">{COLL_LABELS[k] || k}:</span> <span className="font-bold">{v}</span></div>
              ))}
            </div>

            {preview.total_eligible > 0 && (
              <div className="border-t-2 border-amber-500 bg-amber-50 p-4 space-y-3">
                <p className="text-sm text-amber-800">Purged records are moved to a recoverable archive (below) — they are <span className="font-semibold">not</span> permanently deleted. We still recommend downloading a backup first.</p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button data-testid="retention-backup-btn" onClick={downloadBackup} variant="outline" className="rounded-none"><Download size={16} className="mr-1" />Download Backup</Button>
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid="retention-backup-check" type="checkbox" checked={backedUp} onChange={(e) => setBackedUp(e.target.checked)} />
                    I have a backup
                  </label>
                </div>
                <div className="flex gap-2 max-w-md">
                  <Input data-testid="retention-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type PURGE" className="rounded-none border-amber-400 bg-card" />
                  <Button data-testid="run-purge-btn" onClick={runPurge} disabled={busy || !backedUp || confirm.trim() !== "PURGE"}
                    className="rounded-none bg-amber-600 hover:bg-amber-700 whitespace-nowrap"><Trash size={16} className="mr-1" />{busy ? "Purging…" : "Run Purge"}</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="mt-4 border border-border p-3 text-sm" data-testid="retention-purge-result">
            <div className="font-semibold mb-1">Purge complete — {result.archived} archived{result.skipped_due_to_legal_hold ? `, ${result.skipped_due_to_legal_hold} skipped (Legal Hold)` : ""}</div>
            <div className="font-mono text-xs text-muted-foreground">batch {result.batch_id}</div>
          </div>
        )}
      </div>

      {/* Archive */}
      <div className="border border-border bg-card p-5" data-testid="retention-archive">
        <div className="font-display font-bold text-sm mb-3 flex items-center gap-2"><Archive size={16} />Purge Archive (recoverable)</div>
        {batches.length === 0 ? (
          <p className="text-sm text-muted-foreground">No purged batches. Anything you purge appears here and can be restored.</p>
        ) : (
          <div className="divide-y divide-border">
            {batches.map((b) => (
              <div key={b.batch_id} data-testid={`archive-batch-${b.batch_id}`} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">{b.count} record(s) — {Object.entries(b.collections).map(([k, v]) => `${COLL_LABELS[k] || k} ${v}`).join(", ")}</div>
                  <div className="text-xs text-muted-foreground font-mono">{b.purged_at?.slice(0, 16).replace("T", " ")} · by {b.purged_by}</div>
                </div>
                <Button data-testid={`restore-batch-${b.batch_id}`} onClick={() => restoreBatch(b.batch_id)} variant="outline" className="rounded-none whitespace-nowrap">
                  <ClockCounterClockwise size={16} className="mr-1" />Restore
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
